import React, { useCallback } from "react";
import { Button, ButtonGroup, Card } from "react-bootstrap";
import { DashLg, PlusLg, Pencil, Trash } from "react-bootstrap-icons";
import type { ICartDishCardVm } from "@/components/cart/helpers/mapCartDishToCardVm";
import CardActionsDropdown from "@/components/cart/сardActionsDropdown";

interface CartDishCardProps {
  vm: ICartDishCardVm;
  onIncrease: () => void;
  onDecrease: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const CartDishCard: React.FC<CartDishCardProps> = ({
  vm,
  onIncrease,
  onDecrease,
  onEdit,
  onDelete,
}) => {
  const handleEdit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onEdit();
    },
    [onEdit]
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onDelete();
    },
    [onDelete]
  );

  return (
    <Card
      className="rounded-3 border-0 bg-violet-bg p-4"
      style={{ minHeight: "100px" }}
    >
      <div className="d-flex justify-content-between gap-3">
        <div className="min-w-0">
          <div className="fs-14-16">
            <span className="fw-bold me-1">{vm.title}</span>
            <span>{vm.portionSuffix ?? ""}</span>
          </div>

          {vm.addonGroups.length > 0 ? (
            <div className="mt-1 text-grey-text fs-14 d-flex flex-column gap-1">
              {vm.addonGroups.map((group) => (
                <div key={group.groupId}>
                  <div className="fw-bold">{group.groupName}:</div>
                  <div className="d-flex flex-column gap-1 mt-1">
                    {group.lines.map((line, index) => (
                      <div key={`${line.text}-${index}`}>{line.text}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="d-flex flex-column justify-content-between flex-shrink-0 gap-2">
          <div className="d-flex justify-content-end gap-3 gap-md-4">
            <span className="fs-14-16 fw-bold d-flex align-items-center lh-1">
              {vm.totalText}
            </span>
            <div className="d-none d-md-flex gap-3">
              <Button
                type="button"
                variant="link"
                className="p-0"
                onClick={handleEdit}
                aria-label="actions"
              >
                <Pencil size={16} color="#1F3354" />
              </Button>
              <Button
                type="button"
                variant="link"
                className="p-0"
                onClick={handleDelete}
                aria-label="actions"
              >
                <Trash size={16} color="#DC3545" />
              </Button>
            </div>
            <div className="d-flex d-md-none">
              <CardActionsDropdown onEdit={onEdit} onDelete={onDelete} />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <ButtonGroup size="sm">
              <Button
                type="button"
                variant="additional-violet"
                className="rounded-1 border-0 p-0"
                style={{ width: 20, height: 20 }}
                onClick={onDecrease}
                disabled={vm.isUnavailable}
              >
                <DashLg size={10} color="#5842BE" />
              </Button>
              <span className="px-3 fs-14-16 d-flex align-items-center justify-content-center">
                {vm.quantity}
              </span>
              <Button
                type="button"
                variant="additional-violet"
                className="rounded-1 border-0 p-0"
                style={{ width: 20, height: 20 }}
                onClick={onIncrease}
                disabled={vm.isUnavailable}
              >
                <PlusLg size={10} color="#5842BE" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default React.memo(CartDishCard);
